import {Box,Button,Container,Stack,Typography} from "@mui/material";
import {ArrowForwardRounded,CalendarMonthOutlined} from "@mui/icons-material";
import {Link} from "react-router-dom";
import {motion,useReducedMotion} from "framer-motion";
import {useAppTheme} from "../../context/ThemeContext";
import heroImg from "../../static/Home/Hero.webp";

export default function HeroSection(){
  const {colors,typography,shape}=useAppTheme();
  const reduceMotion=useReducedMotion();

  const animation=delay=>({
    initial:reduceMotion?false:{opacity:0,y:18},
    animate:{opacity:1,y:0},
    transition:{
      duration:0.6,
      delay:reduceMotion?0:delay,
      ease:"easeOut"
    }
  });

  const buttonBase={
    minHeight:{xs:44,sm:48,md:50},
    width:"auto",
    maxWidth:"100%",
    px:{xs:2.5,sm:3,md:3.5},
    py:1,
    borderRadius:shape.radius,
    fontSize:{
      xs:typography.sizes.sm,
      md:typography.sizes.md
    },
    fontWeight:500,
    lineHeight:1.4,
    whiteSpace:"nowrap",
    textTransform:"none",
    boxShadow:"none",
    "&:hover":{
      boxShadow:"none"
    }
  };

  return(
    <Box
      component="section"
      aria-label="Plataforma de torneos deportivos"
      sx={{
        position:"relative",
        width:"100%",
        minHeight:{
          xs:590,
          sm:600,
          md:730,
        },
        display:"flex",
        alignItems:"center",
        overflow:"hidden",
        isolation:"isolate",
        backgroundColor:"#06132D"
      }}
    >
      <Box
        component="img"
        src={heroImg}
        alt=""
        aria-hidden="true"
        fetchPriority="high"
        sx={{
          position:"absolute",
          inset:0,
          width:"100%",
          height:"100%",
          objectFit:"cover",
          objectPosition:{
            xs:"62% center",
            sm:"59% center",
            md:"center center"
          },
          zIndex:-3
        }}
      />

      <Box
        sx={{
          position:"absolute",
          inset:0,
          zIndex:-2,
          background:{
            xs:"linear-gradient(180deg,rgba(3,12,35,.80) 0%,rgba(4,15,43,.83) 48%,rgba(3,12,35,.88) 100%)",
            sm:"linear-gradient(180deg,rgba(3,12,35,.75) 0%,rgba(4,15,43,.78) 55%,rgba(3,12,35,.85) 100%)",
            md:"linear-gradient(90deg,rgba(3,12,35,.97) 0%,rgba(4,15,45,.91) 35%,rgba(5,19,54,.57) 64%,rgba(4,15,45,.08) 100%)"
          }
        }}
      />

      <Box
        sx={{
          position:"absolute",
          inset:0,
          zIndex:-1,
          background:{
            xs:"linear-gradient(0deg,rgba(3,12,35,.40),transparent 45%)",
            md:"linear-gradient(0deg,rgba(3,12,35,.45),transparent 45%)"
          }
        }}
      />

      <Container
        maxWidth="xl"
        sx={{
          position:"relative",
          width:"100%",
          px:{
            xs:2.5,
            sm:4,
            md:5,
            lg:7
          },
          py:{
            xs:7,
            sm:9,
            md:12
          }
        }}
      >
        <Box
          sx={{
            width:"100%",
            maxWidth:{
              xs:560,
              sm:680,
              md:800,
              lg:900
            },
            mx:{
              xs:"auto",
              md:0
            },
            display:"flex",
            flexDirection:"column",
            alignItems:{
              xs:"center",
              md:"flex-start"
            },
            textAlign:{
              xs:"center",
              md:"left"
            }
          }}
        >
          <Box
            component={motion.div}
            {...animation(0.05)}
            sx={{
              width:"100%"
            }}
          >
            <Typography
              component="h1"
              sx={{
                m:0,
                fontSize:{
                  xs:"clamp(2.35rem,9vw,3.5rem)",
                  sm:"clamp(3.5rem,7vw,4.8rem)",
                  md:"clamp(4.3rem,6vw,6.2rem)",
                  lg:"clamp(5rem,5.5vw,6.8rem)"
                },
                fontWeight:600,
                lineHeight:{
                  xs:1.12,
                  md:1.06
                },
                letterSpacing:"-0.045em",
                color:"#FFFFFF",
                textAlign:{
                  xs:"center",
                  md:"left"
                },
                textWrap:"balance"
              }}
            >
              La pasión por el fútbol
              <Box
                component="span"
                sx={{
                  display:"block",
                  color:"#3460C9"
                }}
              >
                se vive aquí.
              </Box>
            </Typography>
          </Box>

          <Box
            component={motion.div}
            {...animation(0.15)}
            sx={{
              width:"100%",
              maxWidth:{
                xs:440,
                sm:560,
                md:700
              },
              mt:{
                xs:2.5,
                sm:3,
                md:3.5
              }
            }}
          >
            <Typography
              sx={{
                fontSize:{
                  xs:14,
                  sm:16,
                  md:18
                },
                fontWeight:400,
                lineHeight:{
                  xs:1.75,
                  md:1.85
                },
                color:"#D4DEEF",
                textAlign:{
                  xs:"center",
                  md:"left"
                },
                textWrap:"pretty"
              }}
            >
              Descubre los torneos, sigue cada encuentro y vive
              la emoción de la competencia. Consulta resultados,
              clasificaciones, equipos y estadísticas en un solo lugar.
            </Typography>
          </Box>

          <Stack
            component={motion.div}
            {...animation(0.25)}
            direction="row"
            useFlexGap
            flexWrap="wrap"
            spacing={0}
            sx={{
              width:"100%",
              mt:{
                xs:3.5,
                sm:4,
                md:5
              },
              gap:{
                xs:1.25,
                sm:1.5
              },
              alignItems:"center",
              justifyContent:{
                xs:"center",
                md:"flex-start"
              }
            }}
          >
            <Button
              component={Link}
              to="/torneos/nexus-cup-2026/calendario"
              variant="outlined"
              startIcon={
                <CalendarMonthOutlined
                  sx={{
                    fontSize:"18px !important"
                  }}
                />
              }
              sx={{
                ...buttonBase,
                color:"#FFFFFF",
                borderColor:"rgba(255,255,255,.55)",
                backgroundColor:"transparent",
                "&:hover":{
                  borderColor:"#FFFFFF",
                  backgroundColor:"rgba(255,255,255,.08)",
                  boxShadow:"none"
                }
              }}
            >
              Ver partidos
            </Button>
          </Stack>
        </Box>
      </Container>
    </Box>
  );
}